import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Option } from '@sapphire/result';
import { EMPTY, filter, map, mergeMap, of, switchMap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { SurveyService } from '@ps/survey-data-access';

import { RespondentListService } from '../../services/respondent-list/respondent-list.service';

import { selectSurveyId } from '../core/survey-creation-core.selectors';
import { SurveyCreationCoreApiActions } from '../core/survey-creation-core.actions';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from './survey-creation-launch.actions';

/**
 * Upload/download/view/clear for the Include/Exclude respondent list, plus
 * its activity logging. Split from `SurveyCreationLaunchEffects`
 */
@Injectable()
export class SurveyCreationRespondentListEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _surveyService = inject(SurveyService);
  private readonly _respondentListService = inject(RespondentListService);

  public readonly downloadRespondentListTemplate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationLaunchActions.downloadRespondentListTemplate),
        switchMap(() =>
          this._surveyService
            .downloadRespondentListTemplate()
            .pipe(this.psCatchError(() => EMPTY, { notify: true })),
        ),
      ),
    { dispatch: false },
  );

  // Rehydrates the respondent-list View drawer for a survey that already has an uploaded list
  public readonly loadRespondentListOnSurveyLoaded$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess),
      filter(({ survey }) => !!survey.hasPsidRefList),
      map(({ survey }) =>
        SurveyCreationLaunchActions.loadRespondentList({
          inclExcl: survey.inclExcl === 1 ? 1 : 0,
        }),
      ),
    ),
  );

  public readonly uploadRespondentList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.uploadRespondentList),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      mergeMap(([{ file }, surveyId]) =>
        this._respondentListService
          .upload(Option.from(surveyId).unwrap(), file)
          .pipe(
            map((items) =>
              SurveyCreationLaunchApiActions.uploadRespondentListSuccess({
                items,
              }),
            ),
            this.psCatchError(
              (error) =>
                of(
                  SurveyCreationLaunchApiActions.uploadRespondentListFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public readonly loadRespondentList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.loadRespondentList),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      mergeMap(([{ inclExcl }, surveyId]) =>
        this._respondentListService
          .getUploadedList(surveyId ?? 0, inclExcl)
          .pipe(
            map((items) =>
              SurveyCreationLaunchApiActions.loadRespondentListSuccess({
                items,
              }),
            ),
            this.psCatchError((error) =>
              of(
                SurveyCreationLaunchApiActions.loadRespondentListFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public readonly clearRespondentList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.clearRespondentList),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      mergeMap(([, surveyId]) =>
        this._respondentListService.clear(Option.from(surveyId).unwrap()).pipe(
          map(() =>
            SurveyCreationLaunchApiActions.clearRespondentListSuccess(),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyCreationLaunchApiActions.clearRespondentListFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly logRespondentListUpload$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchApiActions.uploadRespondentListSuccess),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      map(([, surveyId]) =>
        SurveyCreationLaunchActions.logSurveyLaunchActivity({
          surveyId: Option.from(surveyId).unwrap(),
          trigger: { kind: 'includeExcludeListUploaded' },
        }),
      ),
    ),
  );

  // Funnels through the same logSurveyLaunchActivity$ pipeline as the
  // launchFeatures toggles — clearing always represents an ON -> OFF transition.
  public readonly logRespondentListClear$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchApiActions.clearRespondentListSuccess),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      map(([, surveyId]) =>
        SurveyCreationLaunchActions.logSurveyLaunchActivity({
          surveyId: Option.from(surveyId).unwrap(),
          trigger: {
            kind: 'launchToggle',
            field: 'includeExcludeListCleared',
            enabled: false,
          },
        }),
      ),
    ),
  );

  public readonly logIncludeExcludeStatusToggled$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.toggleIncludeExcludeStatus),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      map(([{ enabled }, surveyId]) =>
        SurveyCreationLaunchActions.logSurveyLaunchActivity({
          surveyId: Option.from(surveyId).unwrap(),
          trigger: {
            kind: 'launchToggle',
            field: 'includeExcludeRespondentsEnabled',
            enabled,
          },
        }),
      ),
    ),
  );
}
